/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { postOrQueue } from "../lib/offline";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, NotebookPen, Send, Check, Loader2 } from "lucide-react";

const MOODS = ["😞", "😕", "😐", "🙂", "😄"];
const Chip = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`h-10 px-3 rounded-full text-[12px] font-bold ${active ? "bg-[#0A1426] text-white" : "bg-[#F5F3EE] text-[#0A1426]"}`}>{children}</button>
);

export default function CaregiverNotebookPage() {
  const navigate = useNavigate();
  const { patient, online } = useApp();
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [form, setForm] = useState({ texte: "", humeur: null, fatigue: null, appetit: null });
  const [busy, setBusy] = useState(false);

  const load = () => patient && api.get(`/caregiver-notes?patient_id=${patient.id}`).then(({ data }) => setNotes(data)).catch(() => null);
  useEffect(() => { load(); }, [patient]); // eslint-disable-line

  const save = async (partager) => {
    if (!form.texte.trim()) { toast.error("Écrivez une observation"); return; }
    setBusy(true);
    const res = await postOrQueue("/caregiver-notes", { ...form, patient_id: patient.id, partager, client_date: new Date().toISOString() });
    setBusy(false);
    setForm({ texte: "", humeur: null, fatigue: null, appetit: null });
    if (res?._queued) toast.success("Observation enregistrée — envoyée au retour du réseau");
    else toast.success(partager ? "Observation partagée au praticien" : "Observation enregistrée");
    load();
  };

  const share = async (id) => {
    try { await api.post(`/caregiver-notes/${id}/share`); toast.success("Partagée au praticien"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="bc-in pt-2 pb-6 flex flex-col gap-4" data-testid="caregiver-notebook">
      <div className="flex items-center gap-3">
        <button data-testid="notebook-back" onClick={() => navigate(-1)} className="w-11 h-11 grid place-items-center rounded-full bg-white border border-[#E4E0D5] text-[#0A1426]"><ArrowLeft size={20} /></button>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Carnet de l'aidant</div>
          <h1 className="font-[Fraunces] text-2xl font-bold text-[#0A1426]">{patient?.nom}</h1>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-[#E4E0D5] p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2 text-[#0A1426]"><NotebookPen size={18} className="text-[#2BA3A0]" /><span className="font-bold">Aujourd'hui, j'ai observé…</span></div>
        <Textarea data-testid="notebook-text" value={form.texte} onChange={(e) => setForm({ ...form, texte: e.target.value })}
          placeholder="Ex. : a marché jusqu'à la cuisine sans aide, a peu mangé ce midi, s'est fatigué vite…" className="bg-[#F5F3EE] border-0 min-h-[96px] text-[15px]" />
        <div>
          <div className="text-[11px] font-bold text-[#0A1426]/60 mb-1">Humeur du patient</div>
          <div className="flex gap-2">{MOODS.map((m, i) => <button key={i} data-testid={`notebook-mood-${i + 1}`} onClick={() => setForm({ ...form, humeur: i + 1 })} className={`w-11 h-11 rounded-full text-xl ${form.humeur === i + 1 ? "bg-[#D4AF5A]" : "bg-[#F5F3EE]"}`}>{m}</button>)}</div>
        </div>
        <div>
          <div className="text-[11px] font-bold text-[#0A1426]/60 mb-1">Fatigue</div>
          <div className="flex gap-2">{["faible", "moyenne", "forte"].map((v) => <Chip key={v} testid={`notebook-fatigue-${v}`} active={form.fatigue === v} onClick={() => setForm({ ...form, fatigue: v })}>{v}</Chip>)}</div>
        </div>
        <div>
          <div className="text-[11px] font-bold text-[#0A1426]/60 mb-1">Appétit</div>
          <div className="flex gap-2">{["bon", "moyen", "faible"].map((v) => <Chip key={v} testid={`notebook-appetit-${v}`} active={form.appetit === v} onClick={() => setForm({ ...form, appetit: v })}>{v}</Chip>)}</div>
        </div>
        <div className="flex gap-2 mt-1">
          <Button data-testid="notebook-save-btn" disabled={busy} onClick={() => save(false)} variant="outline" className="flex-1 h-12 rounded-full border-[#0A1426] text-[#0A1426] font-bold">Enregistrer</Button>
          <Button data-testid="notebook-share-btn" disabled={busy} onClick={() => save(true)} className="flex-1 h-12 rounded-full bg-[#2BA3A0] text-white font-bold">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <><Send size={16} className="mr-2" /> Partager au praticien</>}
          </Button>
        </div>
        {!online && <p className="text-[11px] text-[#8A6D2B]">Hors ligne : l'observation sera envoyée au retour du réseau.</p>}
      </div>

      <h3 className="text-[12px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Observations précédentes</h3>
      {notes.length === 0 && <div className="text-[13px] text-[#0A1426]/60">Aucune observation pour l'instant.</div>}
      {notes.map((n) => (
        <div key={n.id} data-testid={`notebook-note-${n.id}`} className="bg-white rounded-2xl border border-[#E4E0D5] p-4">
          <div className="flex items-center justify-between text-[11px] text-[#0A1426]/60">
            <span>{new Date(n.date).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })} · {n.aidant_nom}</span>
            {n.partage ? <span className="inline-flex items-center gap-1 text-[#2d7a2d] font-bold"><Check size={12} /> partagée</span>
              : <button data-testid={`notebook-share-${n.id}`} onClick={() => share(n.id)} className="font-bold text-[#2BA3A0] underline">partager</button>}
          </div>
          <p className="text-[14px] text-[#0A1426] mt-1">{n.texte}</p>
          <div className="flex gap-2 mt-1 text-[11px] text-[#0A1426]/70">
            {n.humeur && <span>{MOODS[n.humeur - 1]} humeur {n.humeur}/5</span>}{n.fatigue && <span>· fatigue {n.fatigue}</span>}{n.appetit && <span>· appétit {n.appetit}</span>}
          </div>
        </div>
      ))}
      {user?.role === "patient" && <p className="text-[11px] text-[#0A1426]/50">Ce carnet est destiné à l'aidant ; il est accessible depuis le Mode Aidant.</p>}
    </div>
  );
}
